import { type ReactNode } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/dialog/dialog'

import { cn } from '@/lib/utils'

export interface ExtractedInfoGuideDialogProps {
  open: boolean
  onChangeOpen: (open: boolean) => void
}

export const ExtractedInfoGuideDialog = ({ open, onChangeOpen }: ExtractedInfoGuideDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onChangeOpen}>
      <DialogContent className={cn('max-w-[600px]', 'max-h-[700px]')}>
        <DialogHeader>
          <DialogTitle>How it works?</DialogTitle>
          <DialogDescription className={cn('sr-only')}>
            This section displays the key details extracted from the document. Please review them for accuracy and
            completeness. If you find any discrepancies, you can edit the information directly in the form fields
            provided below. The extracted information is based on the document&apos;s content and may include fields
            such as applicant name, application filed date, case type, and other relevant data. Ensure that all critical
            information is captured correctly to facilitate further processing.
          </DialogDescription>
        </DialogHeader>

        <div className={cn('')}>
          <div className={cn('mt-4')}>
            <InformationList
              title="Information we extract from your documents"
              items={[
                'Applicant Name (Beneficiary)',
                'Case Number (Receipt Number)',
                'Case Type (i.e. I-130, I-140, etc.)',
                'Service Center',
                'Nationality (Country of Birth)',
                'Priority Date',
                'Valid from to',
                'RFE Response Due Date',
                'Interview Date',
                'Biometric Date',
              ]}
            />
          </div>

          <div className={cn('mt-6')}>
            <InformationList
              title="Information you want to enter manually"
              items={[
                "Applicant's email address",
                'Phone number',
                'Tags (Assign one or more tags to the case, separated by commas)',
                'Form Category (Drop down filtered by Case Type)',
                'Nationality (Country of Birth)',
                'Priority Date',
                'Valid from to',
                'RFE Response Due Date',
                'Interview Date',
                'Biometric Date',
              ]}
            />
          </div>

          <div className={cn('flex flex-col gap-y-5', 'mt-6')}>
            <GuidesByExtractedTypes
              title="New Applicant & New Case:"
              items={[
                <>
                  Lawfully Pro will automatically add the applicant and case, extracting relevant information from your
                  documents.
                </>,
              ]}
            />
            <GuidesByExtractedTypes
              title="Existing Applicant & New Case:"
              items={[
                <>
                  If the applicant already exists in Lawfully Pro but the new case&apos;s document shows a different
                  name, Lawfully Pro will suggest name options based on existing records for you to select. <br />
                  (For example, if Lawfully Pro has &quot;Jon&quot; but the new case document shows &quot;Jonathan&quot;
                  for the same person, the system will recommend using the original name you entered.)
                </>,
              ]}
            />
            <GuidesByExtractedTypes
              title="Existing Applicant & Existing Case:"
              items={[
                <>
                  If both the applicant and case already exist, Lawfully Pro will retain your previously entered
                  information and update only new or missing data. <br />
                  (For example, if the name in the document differs from the one in Lawfully Pro for the same case
                  number, the system will continue showing the original name you entered.)
                </>,
              ]}
            />
          </div>

          <div className={cn('mt-6')}>
            <p className={cn('text-xs font-bold text-black')}>
              We highly recommend you to compare the extracted information to the ones in the uploaded documents!
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const InformationList = ({ title, items }: { title: string; items: string[] }) => {
  return (
    <div>
      <h4 className={cn('text-sm font-semibold text-black')}>{title}</h4>
      <div className={cn('grid auto-rows-auto grid-cols-2 gap-[1px]', 'mt-4')}>
        {items.map((item, index) => (
          <p
            key={index}
            className={cn(
              'text-sm font-medium text-black',
              'px-4 py-2',
              'outline outline-1 outline-slate-200',
              '[&:nth-child(4n)]:bg-slate-100 [&:nth-child(4n-1)]:bg-slate-100'
            )}
          >
            {item}
          </p>
        ))}
      </div>
    </div>
  )
}

const GuidesByExtractedTypes = ({ title, items }: { title: string; items: ReactNode[] }) => {
  return (
    <div>
      <h4 className={cn('text-sm font-medium text-black')}>{title}</h4>
      <ul className={cn('list-outside list-disc', 'ml-5', 'text-xs font-normal text-black', 'mt-1')}>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  )
}
